<template>
  <span class="callback-form__successfull-text" v-if="successCallbackMessage">
    {{ successCallbackMessage }}
  </span>
  <form v-else
        @submit.prevent="sendCallback"
        class="callback__form"
        :class="{ 'callback__form--technical-expert' : isTechnicalExpert }">
    <div class="callback-form__item">
      <AuthPhone
        class="auth-form"
        v-if="!isUserAuth"
        is-ajax-auth
        is-hidden-labels
        :is-code-field-visible="!isTechnicalExpert"
        :remember-field="false"
        :touch-phone-flag="touchPhoneFlag"
        ref="authPhone"
      ></AuthPhone>
      <div v-else class="control-box control-box--phone">
        <PhoneInput v-model.trim="phone" disable-fields />
      </div>
    </div>
    <div class="callback-form__item">
      <Button
        class="callback-form__button"
        type="white-text"
        @click.native="sendCallback"
        :is-disabled="isSubmitButtonDisabled"
      >{{ $t('footer.callback') }}</Button
      >
      <Loader
        class="callback-form__loader"
        v-if="isCallbackLoading"
        :is-loading="isCallbackLoading"
        :border-size="5"
        without-background
      ></Loader>
    </div>
  </form>
</template>

<script>
import PhoneInput from '@/components/inputs/PhoneInput'
import { mapGetters, mapState } from 'vuex'
import Button from '@/components/inputs/Button'
import Loader from '@/components/helpers/Loader'
import AuthPhone from '@/components/auth/AuthPhone'
import qs from 'qs'
import { GoogleTagManagerService } from '@/services/AnalyticService'
import api from '@/api'

export default {
  name: 'ProductCallbackForm',
  data: function () {
    return {
      phone: '',
      isCallbackLoading: false,
      successCallbackMessage: '',
      touchPhoneFlag: ''
    }
  },
  components: {
    Button,
    PhoneInput,
    Loader,
    AuthPhone
  },
  props: {
    isTechnicalExpert: {
      type: Boolean,
      default: false
    },
    productId: {
      type: [String, Number]
    }
  },
  computed: {
    ...mapGetters(['urlLanguagePrefix']),
    ...mapState({
      titleRu: state => state.productInfo.productInfo.titleRu,
      price: state => state.productInfo.productInfo.price,
      code: state => state.productInfo.productInfo.code,
      isUserAuth: state => state.user.auth,
      userData: state => state.user.user_data
    }),
    computedProductId () {
      return this.productId || this.$store.state.productInfo?.productInfo?.productId
    },
    isSubmitButtonDisabled () {
      if (this.isCallbackLoading) return true
      return this.isTechnicalExpert && !this.isUserAuth
    }
  },
  methods: {
    sendCallback () {
      this.touchPhoneFlag = new Date().getTime().toString()
      if (this.phone && !this.isCallbackLoading) {
        this.isCallbackLoading = true

        api.order
          .quickOrder(
            qs.stringify({
              'OneClickForm[phone]': '380' + this.phone,
              'OneClickForm[productId]': this.computedProductId,
              'OneClickForm[isCallback]': 1,
              'OneClickForm[preorder]': 0,
              'OneClickForm[callbackType]': this.isTechnicalExpert ? 'technical_expert' : ''
            })
          )
          .then(data => {
            if (data) {
              this.successCallbackMessage = data.message

              const currentProduct = data.items?.[0]

              if (currentProduct) {
                GoogleTagManagerService.checkoutEvent({
                  analyticsData: [
                    {
                      'name': `${this.titleRu}`,
                      'id': `${this.code}`,
                      'price': `${this.price}`,
                      'category': `${currentProduct.category_parent_id}/${currentProduct.category_id}`,
                      'quantity': 1
                    }
                  ],
                  actionField: { 'step': 6, 'option': 'One Click Product Page Callback' },
                  params: {
                    'event': 'gtm-ee-event-checkout-step-6',
                    'gtm-ee-event-action': 'Checkout Step 6 One Click Product Page Callback'
                  }
                })

                GoogleTagManagerService.purchaseEvent({
                  analyticsData: [
                    {
                      'name': `${this.titleRu}`,
                      'id': `${this.code}`,
                      'price': `${this.price}`,
                      'category': `${currentProduct.category_parent_id}/${currentProduct.category_id}`,
                      'quantity': 1
                    }
                  ],
                  actionField: {
                    'id': data.orderNumber,
                    'affiliation': 'One Click Product Page',
                    'revenue': `${this.price}`
                  },
                  params: {
                    'event': 'gtm-ee-event-purchase',
                    'gtm-ee-event-action': `Purchase`
                  }
                })
              }
            }
          })
          .catch(error => {
            this.$notify({
              group: 'error',
              title: this.$t('error'),
              text: error,
              type: 'error'
            })
          })
          // eslint-disable-next-line no-return-assign
          .finally(() => (this.isCallbackLoading = false))
      }
    }
  },
  watch: {
    userData: {
      immediate: true,
      handler (value) {
        if (value && value.phone) {
          this.phone = value.phone.slice(3)
        }
      }
    }
  }
}
</script>

<style scoped lang="sass">

.callback__form
  position: relative
  margin: 0 auto

.callback__form:not(.callback__form--technical-expert) .callback-form__item
  position: relative
  margin: 0 auto

  &:last-child
    margin: 15px auto 0

    +mmin($tablet)
      margin: 15px 0 0 auto

    +mmin($tablet--hor)
      margin: 0 0 0 15px

  button
    width: 100%

  +mmin($tablet)
    margin: 0 0 0 auto

  +mmin($tablet--hor)
    margin: 0
    &:last-child
      width: max-content

    button
      width: auto

.callback-form__errors-wrap
  text-align: left

  +mmin($tablet--hor)
    position: absolute
    top: 100%
    left: 0

.control-box--phone
  margin-bottom: 0

.callback-form__loader
  transform: translateY(-3px) scale(0.8)

.callback-form__successfull-text
  display: block
  text-align: center
  font-size: 14px
  color: $nd-green-text-color

  +mmin($tablet)
    text-align: right

.callback-form__button
  white-space: nowrap

.auth-form
  ::v-deep
    .form-group
      margin-bottom: 0
    .control-box.control-box--phone
      width: calc(96% - 110px)
      max-width: 170px
    .control-box.control-box--code
      width: auto
      width: 110px
      margin-right: 0
    +mmin($tablet--hor)
      .control-box__help-block--wrapper
        position: absolute
        top: 100%

.callback__form--technical-expert .callback-form__button.vue-button--disabled
  color: #909090
  border-color: #e7e7e7
  cursor: default

</style>
